import type { GroupPublic } from '@repo/types/groups';
import type { ErrorMessage } from '@repo/types/messages/error';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import { countStudents } from '@repo/lib/user-lvl/countStudents';
import { GlobeIcon, UsersIcon } from 'lucide-react';

import type { PostAudienceValue } from '@/hooks/posts';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tag } from '@/components/ui/tags';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

interface PostAudienceTabProps {
  audience: PostAudienceValue;
  onAudienceChange: (value: PostAudienceValue) => void;
  searchGroupValue: string;
  setSearchGroupValue: (value: string) => void;
  selectedGroups: GroupPublic[];
  setSelectedGroups: (groups: GroupPublic[]) => void;
  onRemoveGroup: (id: number) => void;
  groups: GroupPublic[];
  groupsIsEmpty: boolean;
  groupsNoResults: boolean;
  groupsErrorMessage?: ErrorMessage;
  getGroupsInfinite: UseInfiniteQueryResult<InfiniteData<GroupPublic[], unknown>, Error>;
}

export function PostAudienceTab(postAudienceTabProps: PostAudienceTabProps) {
  const { audience, onAudienceChange, setSearchGroupValue, selectedGroups, setSelectedGroups, onRemoveGroup, groups, groupsIsEmpty, groupsNoResults, groupsErrorMessage, getGroupsInfinite } =
    postAudienceTabProps;

  return (
    <div className={'flex grow flex-col gap-6'}>
      <RadioGroup value={audience} onValueChange={(value) => onAudienceChange(value as PostAudienceValue)} className={'grid grid-cols-1 gap-2 sm:grid-cols-2'}>
        <div className="bg-background hover:bg-accent/50 border-border-primary has-data-[state=checked]:border-border-brand relative flex w-full grow cursor-pointer gap-2 rounded-md border p-3 shadow-xs outline-none select-none">
          <RadioGroupItem value={'public'} id={'post-public'} aria-describedby={'post-public-description'} className="static order-1 after:absolute after:inset-0" />
          <div className="flex grow gap-3">
            <FeaturedIcon>
              <GlobeIcon />
            </FeaturedIcon>
            <div className="grid h-fit gap-1">
              <Label htmlFor={'post-public'}>Publiczne</Label>
              <p id={'post-public-description'} className="text-muted-foreground text-xs">
                Posty będą widoczne publicznie dla wszystkich użytkowników.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-background hover:bg-accent/50 border-border-primary has-data-[state=checked]:border-border-brand relative flex w-full grow cursor-pointer gap-2 rounded-md border p-3 shadow-xs outline-none select-none">
          <RadioGroupItem value={'groups'} id={'post-groups'} aria-describedby={'post-groups-description'} className="static order-1 after:absolute after:inset-0" />
          <div className="flex grow gap-3">
            <FeaturedIcon>
              <UsersIcon />
            </FeaturedIcon>
            <div className="grid h-fit gap-1">
              <Label htmlFor={'post-groups'}>Wybrane grupy</Label>
              <p id={'post-groups-description'} className="text-muted-foreground text-xs">
                Posty będą widoczne jedynie dla wybranych grup.
              </p>
            </div>
          </div>
        </div>
      </RadioGroup>

      {audience === 'groups' && (
        <div className="flex grow flex-col gap-3">
          <SearchInput setValue={setSearchGroupValue} placeholder="Wyszukaj..." />
          {selectedGroups.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedGroups.map((group) => (
                <Tag key={group.id} onRemove={() => onRemoveGroup(group.id)} variant={'muted'} border>
                  <Avatar size={'xs'}>
                    <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} />
                    <AvatarFallback>
                      <UsersIcon />
                    </AvatarFallback>
                  </Avatar>
                  {group.name}
                </Tag>
              ))}
            </div>
          )}

          <SelectList
            type={'multiple'}
            selected={selectedGroups.map((g) => String(g.id))}
            setSelected={(ids) => {
              const selectedIds = new Set(ids.map(Number));
              const updatedGroups = [...selectedGroups.filter((g) => selectedIds.has(g.id)), ...groups.filter((g) => selectedIds.has(g.id) && !selectedGroups.some((s) => s.id === g.id))];
              setSelectedGroups(updatedGroups);
            }}
          >
            <SelectListInfiniteQueryStates
              query={getGroupsInfinite}
              isEmpty={groupsIsEmpty}
              noResults={groupsNoResults}
              isEmptyMessage={'Wyszukaj grupę'}
              noResultMessage={'Nie znaleziono grupy'}
              errorMessage={groupsErrorMessage}
            />

            {groups.map((group) => (
              <SelectListElement key={group.id} id={String(group.id)}>
                <Avatar size={'sm'}>
                  <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} />
                  <AvatarFallback>{group.name?.[0] || <UsersIcon />}</AvatarFallback>
                </Avatar>
                <SelectListElementContent>
                  <SelectListElementTitle>{group.name}</SelectListElementTitle>
                  <SelectListElementDescription>
                    {group.slots !== undefined ? (
                      <span className="inline-flex items-center gap-1">
                        Liczba miejsc: {countStudents(group.users ?? [])} / {group.slots}
                      </span>
                    ) : (
                      '-'
                    )}
                  </SelectListElementDescription>
                </SelectListElementContent>
                <SelectListElementTrigger>
                  {({ selected, toggleElement }) => (
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => {
                        toggleElement();
                      }}
                      className="after:absolute after:inset-0"
                    />
                  )}
                </SelectListElementTrigger>
              </SelectListElement>
            ))}
            <InfiniteScroll
              hasNextPage={getGroupsInfinite.hasNextPage}
              isLoading={getGroupsInfinite.isLoading}
              isFetching={getGroupsInfinite.isFetching}
              next={getGroupsInfinite.fetchNextPage}
              threshold={0.65}
            >
              {getGroupsInfinite.hasNextPage && <SelectListElementSkeleton />}
            </InfiniteScroll>
          </SelectList>
        </div>
      )}
    </div>
  );
}
